import { Store } from "shared/src/stores/Store";

import { BusinessAccountResponse } from "../../datatypes/businessaccounts/BusinessAccount";
import BusinessAccountsServiceClient from "../../clients/businessaccounts/BusinessAccountsServiceClient";
import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";
import { RequestStateContainer } from "../utils/RequestStateContainer";
import { LoadState } from "../utils/RequestStateContainer";
import TrackingUtil from "shared/src/utils/TrackingUtil";
import { GdprStorage } from "shared/src/uidispatcherapi/GdprStorage";
import { notify } from "shared/src/utils/Notifications";
import CountryUtil from "shared/src/uidispatcherapi/CountryUtil";
import { getPathFromRedirectParamWithDefault } from "../../utils/redirectUtil";
import { hashString } from "shared/src/utils/util";

type RequestParams = {
    country: string;
    idamToken: string;
};
export type ErrorState = {
    errorOccurred: boolean;
    errorMessage?: string | undefined;
};

/**
 * Retrieves business account data once for a customer.
 */
export class BusinessAccountStoreImpl extends Store {
    container!: RequestStateContainer<BusinessAccountResponse, RequestParams>; // initialized in resetState()
    lastCustomer: string | undefined | null;
    errorState: ErrorState;

    constructor() {
        super();
        this.errorState = {
            errorOccurred: false
        };
        this.listenTo(SessionStore, this.update);
        this.resetState();
        this.update();
    }

    resetState() {
        this.lastCustomer = null;
        this.errorState = {
            errorOccurred: false
        };
        this.container = new RequestStateContainer({
            request: (params: RequestParams) => {
                return BusinessAccountsServiceClient.getBusinessAccounts(params.country, params.idamToken);
            },
            onSuccess: (accountResponse: BusinessAccountResponse) => {
                this.trigger(accountResponse);
            },
            onError: (error) => {
                TrackingUtil.trackUiErrorCa("BusinessAccountStore", "loading business accounts failed", error);
                // For now we do not show a feedback action, because we don't have a good way to decide on which
                // country the business accounts should work (in the ui). (key: CA_LOAD_BUSINESSACCOUNTS_MESSAGES_FAILED)
                this.errorState = {
                    errorOccurred: true,
                    errorMessage: error
                };
                this.trigger();
            },
            transformResponse: (response: any): BusinessAccountResponse => {
                return BusinessAccountResponse.fromJson(response);
            }
        });
    }

    update(): Promise<any> | undefined | null {
        // business accounts don't change often, don't reload it:
        if (this.getLoadState() === "LOADED") {
            return undefined;
        }
        // business accounts are only for customers:
        if (!SessionStore.isCustomer() || !CountryUtil.businessAccountsActive()) {
            return undefined;
        }
        // avoid loading multiple times, just because the SessionStore triggers a lot:
        const currentCustomer = SessionStore.getCustomerId();
        if (this.lastCustomer === currentCustomer || !currentCustomer) {
            return undefined;
        }
        this.lastCustomer = currentCustomer;
        const idamToken = SessionStore.getIdamAccessToken();
        // without an idam token, we would not get data from the businessaccount service anyway:
        if (!idamToken) {
            return undefined;
        }
        const country = SessionStore.getCountry() ? SessionStore.getCountry().toLowerCase() : "de";
        return this.container.load({ country: country, idamToken: idamToken });
    }

    getData(): BusinessAccountResponse | undefined | null {
        return this.container.getState().data;
    }

    getLoadState(): LoadState {
        return this.container.getState().loadState;
    }

    getErrorState(): ErrorState {
        return this.errorState;
    }

    // NOTE: this triggers a full page reload!
    setActiveAccount(newAccountId: string): Promise<any> | undefined | null {
        const idamToken = SessionStore.getIdamAccessToken();
        // without an idam token, we would not get data from the businessaccount service anyway:
        if (!idamToken) {
            return undefined;
        }
        const country = SessionStore.getCountry() ? SessionStore.getCountry().toLowerCase() : "de";
        return BusinessAccountsServiceClient.setActiveAccount(country, idamToken, newAccountId)
            .then(() => {
                const personId = this.container.getState().data?.person.id;
                if (personId) {
                    GdprStorage.localStorage.put(
                        "customer_selected_business",
                        hashString(personId).toString(),
                        GdprStorage.NECESSARY
                    );
                }

                SessionStore.shutdownForExternalRedirect();
                window.location.replace(getPathFromRedirectParamWithDefault("", ""));
            })
            .catch((error) => {
                TrackingUtil.trackUiErrorCa("BusinessAccountStore", "changing business account failed", error);
                const notification = {
                    type: "warning" as const,
                    key: "CA_CHANGE_BUSINESSACCOUNTS_FAILED",
                    values: [],
                    id: "CA_CHANGE_BUSINESSACCOUNTS_FAILED",
                    timeout: 5000
                };
                notify(notification);
            });
    }
}
export const BusinessAccountStore = new BusinessAccountStoreImpl();
